import { Edit } from "@mui/icons-material";
import AddIcon from "@mui/icons-material/Add";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { ClickAwayListener, IconButton, Menu, MenuItem } from "@mui/material";
import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import {
	Project,
	Contact,
	ProjectSaleableUnit,
	ProjectSaleableService,
	ProjectSaleableServiceGroup,
	DiscountProfile,
	TimeLabel,
} from "@repo/server-api";
import {
	BillingAccountType,
	OptionType,
	ProjectPositionType,
	FederalStateType,
} from "@repo/server-api/types";
import { useTranslation } from "next-i18next";
import React, { HTMLAttributes, Key, ReactNode, useState, useMemo, useEffect } from "react";

const filter = createFilterOptions<OptionType>();

export default function Dropdown(
	props: Readonly<{
		element: OptionType | null;
		setElement: (value: OptionType | null) => void;
		error?: string;
		setError?: (value: string) => void;

		doNotShowMenu?: boolean;
		add?: () => void;
		addModify?: () => void;

		dialogValue?: OptionType;
		setDialogValue?: (value: OptionType) => void;

		dropdownType?: string;

		elements:
			| Contact[]
			| ProjectSaleableUnit[]
			| ProjectSaleableServiceGroup[]
			| ProjectSaleableService[]
			| DiscountProfile[]
			| Project[]
			| BillingAccountType[]
			| ProjectPositionType[]
			| TimeLabel[]
			| FederalStateType[];
		noData?: string;

		children?: ReactNode;
		button?: ReactNode;
		searchPlaceholder?: string;
		searchLabel?: string;

		getContacts?: (search: string) => void;
		getProjects?: (search: string) => void;

		readOnly?: boolean;
		"data-testid"?: string;
	}>,
) {
	const { t } = useTranslation();

	const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

	useEffect(() => {
		if (!props.setDialogValue) return;
		props.setDialogValue(props.element ?? { title: "", inputValue: "", item: null });
	}, [props.element, props.setDialogValue]);

	const value = useMemo(() => {
		return props.element ?? { title: "", inputValue: "", item: null };
	}, [props.element]);

	useEffect(() => {
		const elementFound = props.elements.find((e) =>
			(e as Contact)?.displayName
				? (e as Contact)?.displayName === props.element?.title
				: (e as Project)?.name === props.element?.title,
		);
		if (elementFound) {
			props.setElement({
				title:
					(elementFound as Contact)?.displayName ??
					(
						elementFound as
							| Project
							| ProjectSaleableUnit
							| ProjectSaleableServiceGroup
							| ProjectSaleableService
							| DiscountProfile
							| BillingAccountType
							| TimeLabel
							| FederalStateType
					)?.name ??
					(elementFound as ProjectPositionType)?.projectName ??
					"",
				inputValue:
					(elementFound as Contact)?.displayName ??
					(
						elementFound as
							| Project
							| ProjectSaleableUnit
							| ProjectSaleableServiceGroup
							| ProjectSaleableService
							| DiscountProfile
							| BillingAccountType
							| TimeLabel
							| FederalStateType
					)?.name ??
					(elementFound as ProjectPositionType)?.projectName ??
					"",
				item: elementFound ?? null,
			});
		}
	}, [props.elements]);

	const items = useMemo(() => {
		if (!props.elements) return [];
		return (
			props.elements
				.map(
					(
						option:
							| Contact
							| ProjectSaleableUnit
							| ProjectSaleableServiceGroup
							| ProjectSaleableService
							| DiscountProfile
							| Project
							| BillingAccountType
							| ProjectPositionType
							| TimeLabel
							| FederalStateType,
					) => {
						return {
							title:
								(option as Contact)?.displayName ??
								(
									option as
										| Project
										| ProjectSaleableUnit
										| ProjectSaleableServiceGroup
										| ProjectSaleableService
										| DiscountProfile
										| BillingAccountType
										| TimeLabel
										| FederalStateType
								)?.name ??
								(option as ProjectPositionType)?.projectName ??
								null,
							inputValue:
								(option as Contact)?.displayName ??
								(
									option as
										| Project
										| ProjectSaleableUnit
										| ProjectSaleableServiceGroup
										| ProjectSaleableService
										| DiscountProfile
										| BillingAccountType
										| TimeLabel
										| FederalStateType
								)?.name ??
								(option as ProjectPositionType)?.projectName ??
								"",
							item: option,
						};
					},
				)
				// TODO Until backend fixed, remove duplicates
				.filter((e, i, a) => a.findIndex((s) => s.item?.id === e.item?.id) === i)
		);
	}, [props.elements]);

	const handleSearch = (
		event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | EventTarget>,
		element: OptionType | string | null = null,
	) => {
		event.preventDefault();

		const newValue = element ?? (event.target as HTMLInputElement).value;
		if (newValue === null || newValue === undefined) {
			props.setElement?.({ title: "", inputValue: "", item: null });
			setTimeout(() => {
				props.setDialogValue?.({ title: "", inputValue: "", item: null });
			});
		} else if (typeof newValue === "string") {
			if (props.dropdownType === "contact" && props.getContacts) props.getContacts(newValue);
			if (props.dropdownType === "project" && props.getProjects) {
				props.getProjects(newValue);
			}
			props.setElement?.({ title: newValue, inputValue: newValue, item: null });
			setTimeout(() => {
				props.setDialogValue?.({
					title: newValue,
					inputValue: newValue,
					item: null,
				});
			});
		} else if (newValue.item) {
			props.setElement({
				title:
					(newValue.item as Contact).displayName ??
					(newValue.item as Project).name ??
					(newValue.item as ProjectPositionType).projectName ??
					null,
				inputValue:
					(newValue.item as Contact).displayName ??
					(newValue.item as Project).name ??
					(newValue.item as ProjectPositionType).projectName ??
					"",
				item: newValue.item,
			});
			setTimeout(() => {
				props.setDialogValue?.({
					title: newValue.title,
					inputValue: newValue.inputValue,
					item: newValue.item,
				});
			});
		}
		props.setError?.("");
	};

	const setType = (types: string[]) => {
		return types
			.map((type) => {
				const string = type.toLowerCase().split("_").join("");
				return t("contact.contactTypes." + string);
			})
			.join(", ");
	};

	const doNotShowMenu = props.doNotShowMenu ?? false;

	const dropdownType = props.dropdownType ?? "";

	const noData = props.noData ?? t("nothing-available");

	const children = props.children ?? null;
	const button = props.button ?? null;

	return (
		<React.Fragment>
			<Autocomplete
				readOnly={!!props.readOnly}
				value={value}
				onChange={(e, newValue) => handleSearch(e, newValue)}
				filterOptions={(options: OptionType[], params) => {
					let filtered = filter(options, params);

					let isExisting = false;
					if (dropdownType !== "contact" && dropdownType !== "project") {
						isExisting =
							items.filter((e) => e.title?.toLowerCase().includes(value.inputValue.toLowerCase()))
								.length > 0;
					} else {
						// TODO improve for contacts
						isExisting = true;
					}

					if (!isExisting) {
						filtered = [
							{
								title: null,
								item: null,
								inputValue: value.inputValue,
							},
						];
					}

					return filtered;
				}}
				id="free-solo-dialog-demo"
				options={items}
				getOptionLabel={(option) => {
					if (typeof option === "string") {
						return option;
					}

					return option.inputValue;
				}}
				selectOnFocus
				clearOnBlur
				handleHomeEndKeys
				renderOption={(props: HTMLAttributes<HTMLLIElement>, option: OptionType) => {
					const { key, ...optionProps } = props as { key: Key };
					if (option.title === null) {
						return (
							<li key={key || option.item?.id} {...optionProps}>
								<div
									onClick={(e) => {
										e.stopPropagation();
									}}
									onKeyDown={() => {}}>
									<p className="left noPadding w-100">{noData}</p>
									{doNotShowMenu ? "" : button}
									{doNotShowMenu ? "" : children}
								</div>
							</li>
						);
					}

					return (
						<li key={key || option.item?.id} {...optionProps}>
							{dropdownType === "member" ? (
								<div className="memberRow">
									<div className="left w-10">
										<img src={""} alt="avatar" />
									</div>
									<div className="left w-40">{(option.item as Contact)?.displayName}</div>
									<div className="left w-25">
										{setType((option.item as Contact)?.contactTypes ?? [])}
									</div>
									<div className="left w-25">TODO status</div>
								</div>
							) : dropdownType === "projectPosition" ? (
								<div className="memberRow">
									<div className="left w-40">
										{(option.item as ProjectPositionType)?.projectName}
									</div>
									<div className="left w-40">
										{(option.item as ProjectPositionType)?.saleableServiceName}
									</div>
								</div>
							) : (
								option.title
							)}
						</li>
					);
				}}
				className={doNotShowMenu ? "w-100 left" : "w-90 left"}
				freeSolo
				renderInput={(params) => {
					return (
						<TextField
							error={!!props.error}
							helperText={props.error}
							value={value.title}
							{...params}
							onChange={handleSearch}
							placeholder={props.searchPlaceholder}
							label={props.searchLabel}
							data-testid={props["data-testid"]}
						/>
					);
				}}
			/>
			{!doNotShowMenu && (
				<ClickAwayListener
					onClickAway={() => {
						setAnchorEl(null);
					}}>
					<div
						style={{
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
						}}>
						<IconButton
							className="borderNone right"
							onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
								setAnchorEl(e.currentTarget);
							}}>
							<MoreVertIcon className="font" />
						</IconButton>
						<Menu
							disablePortal
							open={Boolean(anchorEl)}
							anchorEl={anchorEl}
							onClose={() => {
								setAnchorEl(null);
							}}>
							<MenuItem>
								<Button
									className="textButton"
									variant="text"
									onClick={() => {
										props.add?.();
										setAnchorEl(null);
									}}>
									<AddIcon /> {t("add")}
								</Button>
							</MenuItem>
							{props.addModify && (
								<MenuItem>
									<Button
										className="textButton"
										variant="text"
										onClick={() => {
											props.addModify?.();
											setAnchorEl(null);
										}}
										disabled={!props.element?.item?.id}>
										<Edit /> {t("modify")}
									</Button>
								</MenuItem>
							)}
						</Menu>
					</div>
				</ClickAwayListener>
			)}
			{children}
		</React.Fragment>
	);
}
